"use client";

import { useMemo } from "react";
import type { DateRange } from "react-day-picker";

import { Calendar } from "../ui/calendar";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

function toYmd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fromYmd(ymd: string): Date {
  // Avoid timezone-shifts by anchoring at midnight local time.
  return new Date(`${ymd}T00:00:00`);
}

export function ReportsDateRangeFilterBar({
  fromDate,
  toDate,
  onChange,
}: {
  fromDate: string | null;
  toDate: string | null;
  onChange: (range: { fromDate: string | null; toDate: string | null }) => void;
}) {
  const selected = useMemo((): DateRange | undefined => {
    const from = fromDate ? fromYmd(fromDate) : undefined;
    const to = toDate ? fromYmd(toDate) : undefined;
    if (!from && !to) return undefined;
    return { from, to };
  }, [fromDate, toDate]);

  const label = useMemo(() => {
    if (!fromDate && !toDate) return "Pick a date range";
    if (fromDate && toDate) return `${fromDate} - ${toDate}`;
    if (fromDate) return `${fromDate} - ...`;
    return `... - ${toDate}`;
  }, [fromDate, toDate]);

  return (
    <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-10 min-h-9 w-full min-w-0 justify-start text-left sm:max-w-md md:w-[280px]"
          >
            {label}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3">
            <Calendar
              mode="range"
              selected={selected}
              onSelect={(range) => {
                const r = range as DateRange | undefined;
                onChange({
                  fromDate: r?.from ? toYmd(r.from) : null,
                  toDate: r?.to ? toYmd(r.to) : null,
                });
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-3 px-3 py-2 border-t">
            <div className="text-sm text-muted-foreground">Optional</div>
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onChange({ fromDate: null, toDate: null })}
            >
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

